import React, { useState } from 'react'
import { Button } from '@/common/ui/button'
import { Progress } from '@/common/ui/progress'
import { ChevronLeft } from 'lucide-react'
import { CouponType } from '@/schema'
import { WizardState, WIZARD_STEPS, RewardType } from './types'
import Step1CouponType from './Step1CouponType'
import Step2RewardType from './Step2RewardType'
import Step3Configuration from './Step3Configuration'

interface BatchFormWizardProps {
	onSubmit: (data: {
		name: string
		description?: string
		requireConnectedLine?: boolean
		quantity?: number
		promotionId?: string
		creditAmount?: number
		maxQuota?: number | null
		type?: CouponType
		customCode?: string
		prefix?: string
	}) => Promise<void>
	isSubmitting: boolean
	onCancel: () => void
}

export default function BatchFormWizard({
	onSubmit,
	isSubmitting,
	onCancel,
}: BatchFormWizardProps) {
	const [wizardState, setWizardState] = useState<WizardState>({
		step: WIZARD_STEPS.COUPON_TYPE,
		couponType: null,
		rewardType: null,
		formData: {
			name: '',
			description: '',
			requireConnectedLine: false,
		},
	})

	const handleCouponTypeSelect = (type: CouponType) => {
		setWizardState((prev) => ({
			...prev,
			couponType: type,
		}))
	}

	const handleRewardTypeSelect = (type: RewardType) => {
		setWizardState((prev) => ({
			...prev,
			rewardType: type,
		}))
	}

	const handleFormChange = (data: Partial<WizardState['formData']>) => {
		setWizardState((prev) => ({
			...prev,
			formData: {
				...prev.formData,
				...data,
			},
		}))
	}

	const nextStep = () => {
		setWizardState((prev) => ({
			...prev,
			step: prev.step + 1,
		}))
	}

	const prevStep = () => {
		setWizardState((prev) => ({
			...prev,
			step: prev.step - 1,
		}))
	}

	const handleSubmit = async () => {
		const { couponType, formData } = wizardState

		const submitData = {
			...formData,
			type: couponType!,
			quantity: couponType === CouponType.SINGLE ? 1 : formData.quantity,
		}

		await onSubmit(submitData)
	}

	const canSubmit =
		wizardState.formData.name &&
		(wizardState.rewardType === 'promotion'
			? wizardState.formData.promotionId
			: wizardState.formData.creditAmount) &&
		(wizardState.couponType === CouponType.SINGLE
			? wizardState.formData.customCode
			: wizardState.formData.prefix && wizardState.formData.quantity)

	const progress = (wizardState.step / 3) * 100

	return (
		<div className="space-y-6">
			{/* Progress Bar */}
			<div className="space-y-2">
				<div className="flex justify-between text-sm">
					<span className="font-medium">
						ขั้นตอนที่ {wizardState.step} จาก 3
					</span>
					<span className="text-muted-foreground">{Math.round(progress)}%</span>
				</div>
				<Progress value={progress} className="h-2" />
			</div>

			{/* Step Content */}
			<div className="min-h-[350px]">
				{wizardState.step === WIZARD_STEPS.COUPON_TYPE && (
					<Step1CouponType
						selectedType={wizardState.couponType}
						onSelect={handleCouponTypeSelect}
						onNext={nextStep}
					/>
				)}

				{wizardState.step === WIZARD_STEPS.REWARD_TYPE && (
					<Step2RewardType
						selectedType={wizardState.rewardType}
						onSelect={handleRewardTypeSelect}
						onNext={nextStep}
					/>
				)}

				{wizardState.step === WIZARD_STEPS.CONFIGURATION && (
					<Step3Configuration
						wizardState={wizardState}
						onFormChange={handleFormChange}
					/>
				)}
			</div>

			{/* Navigation Buttons */}
			<div className="flex justify-between border-t pt-4">
				<div>
					{wizardState.step > WIZARD_STEPS.COUPON_TYPE && (
						<Button
							type="button"
							variant="outline"
							onClick={prevStep}
							disabled={isSubmitting}
						>
							<ChevronLeft className="mr-2 h-4 w-4" />
							ย้อนกลับ
						</Button>
					)}
				</div>

				<div className="flex gap-2">
					<Button type="button" variant="outline" onClick={onCancel}>
						ยกเลิก
					</Button>

					{wizardState.step === WIZARD_STEPS.CONFIGURATION && (
						<Button
							type="button"
							onClick={handleSubmit}
							disabled={!canSubmit || isSubmitting}
						>
							{isSubmitting ? 'กำลังสร้าง...' : 'สร้างคูปอง'}
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
